<script setup lang="ts">
import type { AppFunnelStep, Tip } from '~/types/commons'

definePageMeta({
  layout: 'empty',
  middleware: [function (to) {
    const { query, name } = to
    const { params } = storeToRefs(useEarningsStore())

    // Redirect function of data completion, order matter
    if (name !== 'earnings-owner' && !params.value.vehicle_availability) {
      return navigateTo({ name: 'earnings-owner', query })
    }
    else if (name === 'earnings-owner') {
      return
    }

    if (name !== 'earnings-vehicle' && !params.value.vehicle_type) {
      return navigateTo({ name: 'earnings-vehicle', query })
    }
    else if (name === 'earnings-vehicle') {
      return
    }

    if (name !== 'earnings-vehicle-properties' && (!params.value.number_of_seats || !params.value.vehicle_registered_year || !params.value.vehicle_registration_country)) {
      return navigateTo({ name: 'earnings-vehicle-properties', query })
    }
    else if (name === 'earnings-vehicle-properties') {
      return
    }

    if (name !== 'earnings-about-you' && (params.value.is_professional === null || !params.value.first_name || !params.value.last_name || !params.value.email || !params.value.phone_number)) {
      return navigateTo({ name: 'earnings-about-you', query })
    }
  }],
})

const { t, locale } = useI18n()
const route = useRoute()

const steps: AppFunnelStep[] = [
  {
    id: 'owner',
    routeRawName: 'earnings-owner',
    idForm: 'form-earnings-owner',
    title: t('pages.earnings.owner.step_title'),
    isAvailable: true,
  },
  {
    id: 'vehicle',
    routeRawName: 'earnings-vehicle',
    idForm: 'form-vehicle',
    title: t('pages.earnings.vehicle.step_title'),
    isAvailable: true,
  },
  {
    id: 'vehicle-properties',
    routeRawName: 'earnings-vehicle-properties',
    idForm: 'form-vehicle-properties',
    title: t('pages.earnings.vehicle.step_title'),
    isAvailable: true,
  },
  {
    id: 'about-you',
    routeRawName: 'earnings-about-you',
    idForm: 'form-about-you',
    title: t('pages.earnings.about_you.owner_info'),
    isAvailable: true,
  },
  {
    id: 'results',
    routeRawName: 'earnings-results',
    idForm: 'form-results',
    title: t('pages.earnings.results.step_title'),
    hideFooter: true,
    isAvailable: true,
  },
]

const tipList = computed(() => {
  const list: Tip[] = []
  const atYourService: Tip = {
    title: t('pages.earnings.tips.at_your_service.title'),
    description: t('pages.earnings.tips.at_your_service.description_html'),
    icon: resolveComponent('YscIconsMessages'),
  }

  switch (route.name) {
    case 'earnings-owner':
      list.push(
        {
          title: t('commons.information'),
          description: t('pages.earnings.tips.temporary_documents.description_html'),
          icon: resolveComponent('YscIconsDocument'),
        },
        atYourService,
      )
      break
    case 'earnings-vehicle':
      list.push(
        {
          title: t('commons.information'),
          description: t('pages.earnings.tips.vehicle_type.description_html'),
          icon: resolveComponent('YscIconsVehicle'),
        },
        atYourService,
      )
      break
    case 'earnings-vehicle-properties':
      list.push(
        {
          title: t('commons.information'),
          description: t('pages.earnings.tips.adjust_price.description'),
          icon: resolveComponent('YscIconsPayment'),
        },
        atYourService,
      )
      break
    case 'earnings-about-you':
      list.push(
        {
          title: t('pages.earnings.tips.data_protect.title'),
          description: t('pages.earnings.tips.data_protect.description_html'),
          icon: resolveComponent('YscIconsShield'),
        },
        atYourService,
      )
      break
    case 'earnings-results':
      list.push(
        {
          title: t('pages.earnings.tips.make_profitable.title'),
          description: t('pages.earnings.tips.make_profitable.description_html'),
        },
        {
          title: t('pages.earnings.tips.data_protect.title'),
          description: t('pages.earnings.tips.data_protect.other_description_html'),
          icon: resolveComponent('YscIconsShield'),
        },
        atYourService,
      )
      break
  }
  return list
})

const { currentStep, currentStepIndex, indexedStepsCount, isFirstStep, isLastStep, onStepCancel, onStepSuccess } = useAppFunnel({ steps })

useSeoText({
  title: () => currentStep.value?.title ?? '',
})

const { referential } = storeToRefs(useReferentialStore())
const showGoboonyModal = computed(() => !referential.value?.can_publish_camper)
const { goboonyCreateCamperUrl } = useGoboonyRedirectCreateCamper(locale)
</script>

<template>
  <AppFunnel
    :current-step="currentStep"
    :current-step-index="currentStepIndex"
    :steps-count="indexedStepsCount"
  >
    <template
      v-if="currentStep"
      #main
    >
      <NuxtPage
        :id-form="currentStep.idForm"
        :on-step-success="onStepSuccess"
        keepalive
      />
    </template>

    <template #aside>
      <div class="relative hidden h-full bg-gray-100 md:block">
        <div class="flex flex-col items-center justify-center">
          <YscTipList
            :tip-list="tipList"
            :border="false"
          />
        </div>
      </div>
    </template>

    <template
      v-if="currentStep"
      #footer
    >
      <AppModalYesboonyCreateCamper
        v-if="showGoboonyModal"
        :country="locale"
        :redirect-url="goboonyCreateCamperUrl"
      />

      <template v-if="!isLastStep">
        <button
          v-if="onStepCancel"
          class="btn btn-secondary"
          :disabled="isFirstStep"
          @click="onStepCancel"
        >
          {{ $t('commons.actions.back') }}
        </button>

        <button
          class="btn btn-primary"
          type="submit"
          :form="currentStep.idForm"
        >
          {{ $t('commons.actions.next') }}
        </button>
      </template>
    </template>
  </AppFunnel>
</template>
